// ─────────────────────────────────────────────────────────────────
// PST Date Formatting — Matches Xamarin ConvertCurrentTimeToPSTFormat()
// ─────────────────────────────────────────────────────────────────

/**
 * Returns the current date/time in US Pacific Time.
 *
 * @param format
 *  - `'date'`     → `MM-dd-yyyy`           (TransmissionHeader.TransmissionDateTime)
 *  - `'datetime'` → `yyyy-MM-dd HH:mm:ss`  (UpdateTaskDetail.ActionDatetime)
 */
export function formatPSTDate(format: 'date' | 'datetime'): string {
  const pst = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));

  const year = String(pst.getFullYear());
  const month = String(pst.getMonth() + 1).padStart(2, '0');
  const day = String(pst.getDate()).padStart(2, '0');

  if (format === 'date') {
    return `${month}-${day}-${year}`;
  }

  const hours = String(pst.getHours()).padStart(2, '0');
  const minutes = String(pst.getMinutes()).padStart(2, '0');
  const seconds = String(pst.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// ─────────────────────────────────────────────────────────────────
// Session-scoped completed task filter
// Matches Xamarin DashboardPageViewModel.ApplyTaskListFilter() case 2:
//   taskList.Where(t => ConvertStringToDateTimeFormat(t.LastUpdatedDatetime)
//                       >= _applicationContentService.User.LoginDateTime)
// ─────────────────────────────────────────────────────────────────

/**
 * Parse a PST datetime string in `"yyyy-MM-dd HH:mm:ss"` format (as returned
 * by the OIC middleware API) into a Date. Returns `null` for invalid input.
 */
function parsePSTDatetime(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Expected format: "yyyy-MM-dd HH:mm:ss"
  const parts = dateStr.split(' ');
  if (parts.length !== 2) return null;

  const dateParts = parts[0]?.split('-');
  const timeParts = parts[1]?.split(':');
  if (dateParts?.length !== 3 || timeParts?.length !== 3) return null;

  const year = Number(dateParts[0]);
  const month = Number(dateParts[1]);
  const day = Number(dateParts[2]);
  const hours = Number(timeParts[0]);
  const minutes = Number(timeParts[1]);
  const seconds = Number(timeParts[2]);

  if ([year, month, day, hours, minutes, seconds].some((v) => isNaN(v))) return null;

  return new Date(year, month - 1, day, hours, minutes, seconds);
}

/**
 * Check whether a task's `LastUpdatedDatetime` falls after the current login time.
 *
 * Matches Xamarin DashboardPageViewModel.ApplyTaskListFilter() case 2:
 *   taskList.Where(t => ConvertStringToDateTimeFormat(t.LastUpdatedDatetime)
 *                       >= _applicationContentService.User.LoginDateTime)
 *
 * - `lastUpdatedDatetime`: `"yyyy-MM-dd HH:mm:ss"` from OIC API (PST)
 *
 * Falls back to start-of-day (PST) if login time is unavailable.
 * Returns `true` (fail-open) when the date cannot be parsed.
 */
export function isTaskInCurrentSession(lastUpdatedDatetime: string): boolean {
  const taskTime = parsePSTDatetime(lastUpdatedDatetime);
  if (!taskTime) return true;

  const loginRaw = localStorage.getItem('yms_login_date_time');
  if (loginRaw) {
    try {
      const loginTime = new Date(JSON.parse(loginRaw) as string);
      const loginPST = new Date(
        loginTime.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
      );
      return taskTime.getTime() >= loginPST.getTime();
    } catch {
      // Fall through to start-of-day fallback
    }
  }

  const nowPST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const startOfDayPST = new Date(nowPST.getFullYear(), nowPST.getMonth(), nowPST.getDate());
  return taskTime.getTime() >= startOfDayPST.getTime();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFn = (key: string, options?: any) => string;

export function formatTimeAgo(dateTime: string, t: TranslateFn): string {
  const created = new Date(dateTime);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();

  if (diffMs < 0) return t('timeAgo.justNow');

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffSeconds <= 60) return t('timeAgo.secondsAgo', { count: diffSeconds });
  if (diffMinutes === 1) return t('timeAgo.aMinuteAgo');
  if (diffMinutes < 60) return t('timeAgo.minutesAgo', { count: diffMinutes });
  if (diffHours === 1) return t('timeAgo.anHourAgo');
  if (diffHours < 24) return t('timeAgo.hoursAgo', { count: diffHours });
  if (diffDays === 1) return t('timeAgo.yesterday');
  if (diffDays < 30) return t('timeAgo.daysAgo', { count: diffDays });
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? t('timeAgo.aMonthAgo') : t('timeAgo.monthsAgo', { count: months });
  }
  const years = Math.floor(diffDays / 365);
  return years === 1 ? t('timeAgo.aYearAgo') : t('timeAgo.yearsAgo', { count: years });
}

export function convertPSTToIST(pstDateTime: string): Date {
  const parsed = new Date(pstDateTime);
  const pstStr = parsed.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
  const pstAsLocal = new Date(pstStr);
  const localToPstCorrection = parsed.getTime() - pstAsLocal.getTime();
  const utcMs = parsed.getTime() + localToPstCorrection;
  const istStr = new Date(utcMs).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
  return new Date(istStr);
}
